import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * LoaderContext
 * Global loading state management for API calls
 * Provides showLoader/hideLoader functions and isLoading state
 * Used to display a full-screen loader during API requests (except forms)
 */

// Create the context
const LoaderContext = createContext({
    isLoading: false,
    loadingCount: 0,
    showLoader: () => { },
    hideLoader: () => { },
});

/**
 * LoaderProvider Component
 * Wraps the app and provides global loading state
 * Uses a counter to handle multiple simultaneous API calls
 */
export const LoaderProvider = ({ children }) => {
    // Use a counter to track multiple simultaneous requests
    const [loadingCount, setLoadingCount] = useState(0);

    // Increment loading counter - shows loader
    const showLoader = useCallback(() => {
        setLoadingCount(prev => prev + 1);
    }, []);

    // Decrement loading counter - hides loader when count reaches 0
    const hideLoader = useCallback(() => {
        setLoadingCount(prev => Math.max(0, prev - 1));
    }, []);

    // Loader is visible when count > 0
    const isLoading = loadingCount > 0;

    const value = {
        isLoading,
        loadingCount,
        showLoader,
        hideLoader,
    };

    return (
        <LoaderContext.Provider value={value}>
            {children}
        </LoaderContext.Provider>
    );
};

/**
 * useLoader Hook
 * Custom hook to access loader context
 */
export const useLoader = () => {
    const context = useContext(LoaderContext);
    if (!context) {
        throw new Error('useLoader must be used within a LoaderProvider');
    }
    return context;
};

export default LoaderContext;
